/** Thin fetch wrapper. Admin calls carry the session cookie; portal calls carry the token header. */
export interface ApiError {
  code: string
  message: string
  details?: unknown
  requestId: string
}

export class ApiRequestError extends Error {
  constructor(public readonly status: number, public readonly error: ApiError) {
    super(error.message)
    this.name = 'ApiRequestError'
  }
}

/** The company access token lives in memory only — never localStorage (FRM-16, NFR-S2). */
let portalToken: string | null = null
export function setPortalToken(token: string | null): void {
  portalToken = token
}
export function getPortalToken(): string | null {
  return portalToken
}

/** Identifies this browser tab so the server can flag concurrent editing of a tab. */
export const sessionId = Math.random().toString(36).slice(2, 12)

export async function api<T>(path: string, init: RequestInit & { portal?: boolean } = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json')
  if (init.portal !== false && portalToken) {
    headers.set('x-datahub-token', portalToken)
    headers.set('x-session-id', sessionId)
  }

  const res = await fetch(`/api/v1${path}`, { ...init, headers, credentials: 'include' })
  if (res.status === 204) return undefined as T

  const text = await res.text()
  const body = text ? JSON.parse(text) : {}
  if (!res.ok) {
    throw new ApiRequestError(res.status, body.error ?? { code: 'unknown', message: 'Something went wrong', requestId: '' })
  }
  return body as T
}

export const get = <T>(path: string) => api<T>(path)
export const post = <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined })
export const patch = <T>(path: string, body: unknown) => api<T>(path, { method: 'PATCH', body: JSON.stringify(body) })
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' })

/** Downloads a CSV through an authenticated request rather than a bare link. */
export async function downloadCsv(path: string, fallbackName: string): Promise<void> {
  const res = await fetch(`/api/v1${path}`, { credentials: 'include' })
  if (!res.ok) throw new Error('That export could not be produced')
  const blob = await res.blob()
  const disposition = res.headers.get('content-disposition') ?? ''
  const match = /filename="([^"]+)"/.exec(disposition)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = match?.[1] ?? fallbackName
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}
